/**
 * WORKFLOW STORE
 *
 * Zustand store for managing Agent Workflows.
 * Persists to backend API (/api/workflows) or SQLite via DataSource.
 * Falls back to hardcoded defaults if API is unavailable.
 */

import { create } from "zustand";
import type { WorkflowConfig, StageLabelMap, StageActions, StageLabelMapEntry, StageActionsEntry, PipelineStage } from "../../../shared/schema";
import { getDataSource } from "@/lib/data";
import { useWorkspaceStore, isLocalWorkspace } from "@/stores/workspaceStore";
import { workflowToApi, apiToWorkflow } from "@/lib/data/mappers";

export type NodeType =
  | "start"
  | "plan"
  | "subtasks"
  | "ui_concept"
  | "code"
  | "test"
  | "human_review"
  | "llm_review"
  | "deploy"
  | "end";

export type IssueType = "wp" | "bug" | "feature" | "frontend" | "hotfix" | "refactor";

export interface WorkflowNode {
  id: string;
  type: NodeType;
  label: string;
  x: number;
  y: number;
  config: NodeConfig;
}

export interface NodeConfig {
  timeout?: number;
  maxRetries?: number;
  humanApprovalRequired?: boolean;
  autoAdvance?: boolean;
  description?: string;
  // Start node
  startRequiredFields?: string[];
  // AI provider config
  provider?: "claude" | "codex" | "gemini";
  model?: string;
  reasoningEffort?: "low" | "medium" | "high" | "xhigh";
  systemPrompt?: string;
  // Test node
  testCommand?: string;
  // Deploy node
  createDraftPr?: boolean;
  // LLM review node
  llmReviewMaxIterations?: number;
  llmReviewAutoFix?: boolean;
  // explicit rerun target for llm_review nodes
  reviewTargetNodeId?: string;
  llmReviewBlockOnFail?: boolean;
}

export const providerModels = {
  claude: {
    label: "Claude",
    models: [
      { value: "claude-opus-4-6", label: "Claude Opus 4.6" },
      { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
      { value: "claude-opus-4-5", label: "Claude Opus 4.5" },
      { value: "claude-sonnet-4-5", label: "Claude Sonnet 4.5" },
      { value: "claude-haiku-4-5", label: "Claude Haiku 4.5" },
      { value: "claude-opus-4-1", label: "Claude Opus 4.1 (Legacy)" },
      { value: "claude-sonnet-4-0", label: "Claude Sonnet 4.0 (Legacy)" },
    ],
  },
  codex: {
    label: "Codex",
    models: [
      { value: "gpt-5.3-codex", label: "Codex 5.3" },
      { value: "gpt-5.2-codex", label: "GPT-5.2 Codex" },
      { value: "gpt-5.1-codex", label: "GPT-5.1 Codex" },
      { value: "gpt-5.1-codex-max", label: "GPT-5.1 Codex Max" },
      { value: "gpt-5-codex", label: "GPT-5 Codex" },
      { value: "gpt-5.1-codex-mini", label: "GPT-5.1 Codex Mini" },
      { value: "codex-mini-latest", label: "codex-mini-latest (Deprecated)" },
    ],
  },
  gemini: {
    label: "Gemini",
    models: [
      { value: "gemini-3-pro-preview", label: "Gemini 3 Pro Preview" },
      { value: "gemini-3-flash-preview", label: "Gemini 3 Flash Preview" },
      { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
      { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
      { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash-Lite" },
    ],
  },
} as const;

export type AIProvider = keyof typeof providerModels;

export interface NodeConnection {
  id: string;
  sourceId: string;
  targetId: string;
  condition?: string;
}

export interface Workflow {
  id: string;
  name: string;
  description: string;
  issueType: IssueType;
  nodes: WorkflowNode[];
  connections: NodeConnection[];
  isDefault: boolean;
  workspaceId?: number;
  version?: number;
  createdAt: string;
  updatedAt: string;
  stageLabelMap?: StageLabelMap;
  stageActions?: StageActions;
}

export interface NodeTemplate {
  type: NodeType;
  label: string;
  description: string;
  icon: string;
  color: string;
  defaultConfig: NodeConfig;
}

const DEFAULT_NODE_SYSTEM_PROMPTS = {
  plan: "You are a senior software architect. Produce a concise implementation plan grounded in the current codebase, with explicit steps, files, and risks.",
  subtasks: "Break the task into small, executable subtasks with clear completion criteria and practical ordering.",
  code: "Make minimal, production-safe code changes that match existing patterns. Avoid unrelated refactors and include or update tests when behavior changes.",
  llmReview: "Review strictly for correctness, regressions, security, and missing tests. Return PASS only when no blocking issues remain.",
} as const;

export const nodeTemplates: NodeTemplate[] = [
  {
    type: "start",
    label: "Start",
    description: "Workflow entry point",
    icon: "Play",
    color: "#22c55e",
    defaultConfig: {},
  },
  {
    type: "plan",
    label: "Plan",
    description: "Agent creates implementation plan",
    icon: "FileText",
    color: "#3b82f6",
    defaultConfig: {
      timeout: 1800,
      maxRetries: 2,
      provider: "claude",
      model: "claude-sonnet-4-5",
      systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.plan,
    },
  },
  {
    type: "subtasks",
    label: "Subtasks",
    description: "Split work into smaller tasks",
    icon: "ListTodo",
    color: "#8b5cf6",
    defaultConfig: {
      timeout: 1800,
      provider: "claude",
      model: "claude-sonnet-4-5",
      systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.subtasks,
    },
  },
  {
    type: "ui_concept",
    label: "UI Design",
    description: "Agent creates UI/UX design document",
    icon: "Palette",
    color: "#ec4899",
    defaultConfig: {
      timeout: 1800,
      provider: "claude",
      model: "claude-opus-4-6",
      systemPrompt: "You are a senior UI/UX designer. Produce a structured design document - NO code. Include component hierarchy, layout, responsive breakpoints, interactions, and design tokens referencing existing codebase patterns.",
    },
  },
  {
    type: "code",
    label: "Code",
    description: "Agent writes/modifies code",
    icon: "Code",
    color: "#f59e0b",
    defaultConfig: {
      timeout: 3600,
      maxRetries: 3,
      provider: "claude",
      model: "claude-opus-4-6",
      reasoningEffort: "high",
      systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.code,
    },
  },
  {
    type: "test",
    label: "Test",
    description: "Run automated tests",
    icon: "TestTube",
    color: "#06b6d4",
    defaultConfig: { timeout: 0, autoAdvance: true },
  },
  {
    type: "human_review",
    label: "Human Review",
    description: "Wait for human approval",
    icon: "Users",
    color: "#ec4899",
    defaultConfig: { humanApprovalRequired: true },
  },
  {
    type: "llm_review",
    label: "LLM Review",
    description: "Another AI reviews the work",
    icon: "Bot",
    color: "#6366f1",
    defaultConfig: {
      timeout: 1800,
      provider: "codex",
      model: "gpt-5.3-codex",
      reasoningEffort: "high",
      systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.llmReview,
      llmReviewMaxIterations: 4,
      llmReviewAutoFix: true,
      llmReviewBlockOnFail: true,
    },
  },
  {
    type: "deploy",
    label: "Deploy",
    description: "Deploy changes",
    icon: "Rocket",
    color: "#10b981",
    defaultConfig: { createDraftPr: true },
  },
  {
    type: "end",
    label: "End",
    description: "Workflow completion",
    icon: "CheckCircle",
    color: "#64748b",
    defaultConfig: {},
  },
];

export const issueTypeConfig: Record<IssueType, { label: string; description: string; color: string }> = {
  wp: {
    label: "Work Package",
    description: "Large features requiring multiple days of work",
    color: "#3b82f6"
  },
  feature: {
    label: "Feature",
    description: "New functionality additions",
    color: "#22c55e"
  },
  frontend: {
    label: "Frontend/Design",
    description: "UI/UX design and frontend implementation",
    color: "#ec4899"
  },
  bug: {
    label: "Bug",
    description: "Bug fixes and issue resolution",
    color: "#ef4444"
  },
  hotfix: {
    label: "Hotfix",
    description: "Urgent production fixes",
    color: "#f97316"
  },
  refactor: {
    label: "Refactor",
    description: "Code improvements without feature changes",
    color: "#8b5cf6"
  },
};

// Hardcoded fallback defaults (used before API loads)
const fallbackWorkflows: Workflow[] = [
  {
    id: "wf-bug-default",
    name: "Bug Fix (Default)",
    description: "Quick workflow for bug fixes",
    issueType: "bug",
    isDefault: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    nodes: [
      { id: "n1", type: "start", label: "Start", x: 100, y: 200, config: {} },
      { id: "n2", type: "plan", label: "Diagnose", x: 250, y: 200, config: { timeout: 1800, systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.plan } },
      { id: "n3", type: "code", label: "Fix Bug", x: 400, y: 200, config: { timeout: 3600, provider: "claude", model: "claude-opus-4-6", reasoningEffort: "high", systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.code } },
      { id: "n4", type: "test", label: "Verify Fix", x: 550, y: 200, config: {} },
      { id: "n5", type: "deploy", label: "Deploy", x: 700, y: 200, config: {} },
      { id: "n6", type: "end", label: "Complete", x: 850, y: 200, config: {} },
    ],
    connections: [
      { id: "c1", sourceId: "n1", targetId: "n2" },
      { id: "c2", sourceId: "n2", targetId: "n3" },
      { id: "c3", sourceId: "n3", targetId: "n4" },
      { id: "c4", sourceId: "n4", targetId: "n5" },
      { id: "c5", sourceId: "n5", targetId: "n6" },
    ],
  },
  {
    id: "wf-frontend-default",
    name: "Frontend/Design (Default)",
    description: "Workflow for UI/UX design and frontend tasks",
    issueType: "frontend",
    isDefault: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    nodes: [
      { id: "n1", type: "start", label: "Start", x: 100, y: 200, config: {} },
      { id: "n2", type: "plan", label: "Plan", x: 250, y: 200, config: { timeout: 1800, systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.plan } },
      { id: "n3", type: "ui_concept", label: "UI Design", x: 400, y: 200, config: { timeout: 1800, provider: "claude", model: "claude-opus-4-6" } },
      { id: "n4", type: "code", label: "Implement", x: 550, y: 200, config: { timeout: 3600, provider: "claude", model: "claude-sonnet-4-6", systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.code } },
      {
        id: "n5",
        type: "llm_review",
        label: "AI Review",
        x: 700,
        y: 200,
        config: {
          provider: "codex",
          model: "gpt-5.3-codex",
          reasoningEffort: "high",
          systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.llmReview,
          llmReviewMaxIterations: 3,
          llmReviewAutoFix: true,
          llmReviewBlockOnFail: true,
        },
      },
      { id: "n6", type: "deploy", label: "Deploy", x: 850, y: 200, config: {} },
      { id: "n7", type: "end", label: "Complete", x: 1000, y: 200, config: {} },
    ],
    connections: [
      { id: "c1", sourceId: "n1", targetId: "n2" },
      { id: "c2", sourceId: "n2", targetId: "n3" },
      { id: "c3", sourceId: "n3", targetId: "n4" },
      { id: "c4", sourceId: "n4", targetId: "n5" },
      { id: "c5", sourceId: "n5", targetId: "n6" },
      { id: "c6", sourceId: "n6", targetId: "n7" },
    ],
  },
  {
    id: "wf-feature-default",
    name: "Feature (Default)",
    description: "Standard workflow for new features",
    issueType: "feature",
    isDefault: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    nodes: [
      { id: "n1", type: "start", label: "Start", x: 100, y: 200, config: {} },
      { id: "n2", type: "plan", label: "Plan Feature", x: 250, y: 200, config: { timeout: 1800, systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.plan } },
      { id: "n3", type: "code", label: "Implement", x: 400, y: 200, config: { timeout: 3600, provider: "claude", model: "claude-opus-4-6", reasoningEffort: "high", systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.code } },
      { id: "n4", type: "test", label: "Test", x: 550, y: 200, config: {} },
      {
        id: "n5",
        type: "llm_review",
        label: "AI Review",
        x: 700,
        y: 200,
        config: {
          provider: "codex",
          model: "gpt-5.3-codex",
          reasoningEffort: "high",
          systemPrompt: DEFAULT_NODE_SYSTEM_PROMPTS.llmReview,
          llmReviewMaxIterations: 4,
          llmReviewAutoFix: true,
          llmReviewBlockOnFail: true,
        },
      },
      { id: "n6", type: "deploy", label: "Deploy", x: 850, y: 200, config: {} },
      { id: "n7", type: "end", label: "Complete", x: 1000, y: 200, config: {} },
    ],
    connections: [
      { id: "c1", sourceId: "n1", targetId: "n2" },
      { id: "c2", sourceId: "n2", targetId: "n3" },
      { id: "c3", sourceId: "n3", targetId: "n4" },
      { id: "c4", sourceId: "n4", targetId: "n5" },
      { id: "c5", sourceId: "n5", targetId: "n6" },
      { id: "c6", sourceId: "n6", targetId: "n7" },
    ],
  },
];

interface WorkflowState {
  workflows: Workflow[];
  selectedWorkflowId: string | null;
  selectedNodeId: string | null;
  isEditing: boolean;
  isLoading: boolean;
  isSaving: boolean;

  selectWorkflow: (id: string | null) => void;
  selectNode: (id: string | null) => void;
  setEditing: (editing: boolean) => void;

  addWorkflow: (workflow: Omit<Workflow, "id" | "createdAt" | "updatedAt">) => void;
  updateWorkflow: (id: string, updates: Partial<Workflow>) => void;
  deleteWorkflow: (id: string) => void;
  duplicateWorkflow: (id: string) => void;
  saveWorkflow: (id: string) => Promise<void>;

  addNode: (workflowId: string, node: Omit<WorkflowNode, "id">) => void;
  updateNode: (workflowId: string, nodeId: string, updates: Partial<WorkflowNode>) => void;
  deleteNode: (workflowId: string, nodeId: string) => void;
  moveNode: (workflowId: string, nodeId: string, x: number, y: number) => void;

  addConnection: (workflowId: string, sourceId: string, targetId: string) => void;
  deleteConnection: (workflowId: string, connectionId: string) => void;

  getWorkflowForIssueType: (issueType: IssueType) => Workflow | undefined;
  fetchWorkflows: () => Promise<void>;
  getLabelsForStage: (stage: PipelineStage, workflowId?: string) => StageLabelMapEntry | undefined;
  getActionsForStage: (stage: PipelineStage, workflowId?: string) => StageActionsEntry | undefined;
  resolveWorkflowForTask: (task: any) => WorkflowConfig | undefined;
}

async function getActiveDs() {
  const workspaceState = useWorkspaceStore.getState();
  const wsType = workspaceState.getActiveWorkspaceType();

  if (wsType === "local") {
    const activeWorkspaceId = workspaceState.activeWorkspaceId;
    if (!isLocalWorkspace(activeWorkspaceId)) {
      throw new Error("No active local workspace selected");
    }

    const { setActiveLocalWorkspace } = await import("@/lib/data/local-source");
    setActiveLocalWorkspace(activeWorkspaceId);
  }

  return getDataSource(wsType);
}

function resolveWorkflowWorkspaceId(explicit?: number | string): number | string | null {
  if (explicit !== undefined && explicit !== null && explicit !== "") return explicit;

  const workspaceState = useWorkspaceStore.getState();
  const wsType = workspaceState.getActiveWorkspaceType();
  if (wsType === "local") {
    return workspaceState.activeWorkspaceId || null;
  }

  return workspaceState.getActiveWorkspaceBackendId();
}

export const useWorkflowStore = create<WorkflowState>()(
  (set, get) => ({
    workflows: fallbackWorkflows,
    selectedWorkflowId: null,
    selectedNodeId: null,
    isEditing: false,
    isLoading: false,
    isSaving: false,

    selectWorkflow: (id) => set({ selectedWorkflowId: id, selectedNodeId: null }),
    selectNode: (id) => set({ selectedNodeId: id }),
    setEditing: (editing) => set({ isEditing: editing }),

    addWorkflow: (workflow) => {
      const resolvedWorkspaceId = resolveWorkflowWorkspaceId(workflow.workspaceId);
      const newWorkflow: Workflow = {
        ...workflow,
        id: `wf-${Date.now()}`,
        // @ts-expect-error - resolveWorkflowWorkspaceId can return string | number; pre-existing, gate-quarantined (see CLAUDE.md)
        workspaceId: resolvedWorkspaceId ?? undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      set((state) => ({ workflows: [...state.workflows, newWorkflow] }));

      // Persist via DataSource
      const wfData = workflowToApi(newWorkflow);
      if (resolvedWorkspaceId != null) {
        getActiveDs().then((ds) =>
          ds.workflows.create({
            workspaceId: resolvedWorkspaceId,
            name: wfData.name,
            issueType: wfData.issue_type,
            isDefault: wfData.is_default,
            graph: wfData.graph,
          })
        )
          .then((resp: any) => {
            set((state) => ({
              workflows: state.workflows.map((w) =>
                w.id === newWorkflow.id ? apiToWorkflow(resp) : w
              ),
            }));
          })
          .catch((err: any) => console.error("Failed to save workflow:", err));
      }
    },

    updateWorkflow: (id, updates) => {
      set((state) => ({
        workflows: state.workflows.map((wf) =>
          wf.id === id ? { ...wf, ...updates, updatedAt: new Date().toISOString() } : wf
        ),
      }));
    },

    saveWorkflow: async (id) => {
      const workflow = get().workflows.find((w) => w.id === id);
      if (!workflow) return;

      set({ isSaving: true });
      try {
        const data = workflowToApi(workflow);
        const ds = await getActiveDs();
        const resolvedWorkspaceId = resolveWorkflowWorkspaceId(workflow.workspaceId);
        const isEphemeralWorkflow = id.startsWith("wf-");

        if (isEphemeralWorkflow) {
          if (resolvedWorkspaceId == null) {
            throw new Error("Cannot save workflow: no active workspace id");
          }
          const created = await ds.workflows.create({
            workspaceId: resolvedWorkspaceId,
            name: data.name,
            issueType: data.issue_type,
            isDefault: data.is_default,
            graph: data.graph,
          });
          const persisted = apiToWorkflow(created);
          set((state) => ({
            workflows: state.workflows.map((w) => (w.id === id ? persisted : w)),
            selectedWorkflowId: state.selectedWorkflowId === id ? persisted.id : state.selectedWorkflowId,
          }));
          return;
        }

        await ds.workflows.update(id, {
          name: data.name,
          issueType: data.issue_type,
          isDefault: data.is_default,
          graph: data.graph,
        });
      } catch (err) {
        console.error("Failed to save workflow:", err);
      } finally {
        set({ isSaving: false });
      }
    },

    deleteWorkflow: (id) => {
      set((state) => ({
        workflows: state.workflows.filter((wf) => wf.id !== id),
        selectedWorkflowId: state.selectedWorkflowId === id ? null : state.selectedWorkflowId,
      }));
      getActiveDs().then((ds) => ds.workflows.delete(id))
        .catch((err: any) => console.error("Failed to delete workflow:", err));
    },

    duplicateWorkflow: (id) => {
      const workflow = get().workflows.find((wf) => wf.id === id);
      if (!workflow) return;
      const resolvedWorkspaceId = resolveWorkflowWorkspaceId(workflow.workspaceId);

      const newWorkflow: Workflow = {
        ...workflow,
        id: `wf-${Date.now()}`,
        name: `${workflow.name} (Copy)`,
        isDefault: false,
        // @ts-expect-error - resolveWorkflowWorkspaceId can return string | number; pre-existing, gate-quarantined (see CLAUDE.md)
        workspaceId: resolvedWorkspaceId ?? undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      set((state) => ({ workflows: [...state.workflows, newWorkflow] }));

      // Persist copy
      if (resolvedWorkspaceId != null) {
        const wfData = workflowToApi(newWorkflow);
        getActiveDs().then((ds) =>
          ds.workflows.create({
            workspaceId: resolvedWorkspaceId,
            name: wfData.name,
            issueType: wfData.issue_type,
            isDefault: wfData.is_default,
            graph: wfData.graph,
          })
        )
          .then((resp: any) => {
            set((state) => ({
              workflows: state.workflows.map((w) =>
                w.id === newWorkflow.id ? apiToWorkflow(resp) : w
              ),
            }));
          })
          .catch((err: any) => console.error("Failed to save duplicated workflow:", err));
      }
    },

    addNode: (workflowId, node) => {
      const newNode: WorkflowNode = {
        ...node,
        id: `node-${Date.now()}`,
      };
      set((state) => ({
        workflows: state.workflows.map((wf) =>
          wf.id === workflowId
            ? { ...wf, nodes: [...wf.nodes, newNode], updatedAt: new Date().toISOString() }
            : wf
        ),
      }));
    },

    updateNode: (workflowId, nodeId, updates) => {
      set((state) => ({
        workflows: state.workflows.map((wf) =>
          wf.id === workflowId
            ? {
                ...wf,
                nodes: wf.nodes.map((n) => (n.id === nodeId ? { ...n, ...updates } : n)),
                updatedAt: new Date().toISOString(),
              }
            : wf
        ),
      }));
    },

    deleteNode: (workflowId, nodeId) => {
      set((state) => ({
        workflows: state.workflows.map((wf) =>
          wf.id === workflowId
            ? {
                ...wf,
                nodes: wf.nodes.filter((n) => n.id !== nodeId),
                connections: wf.connections.filter(
                  (c) => c.sourceId !== nodeId && c.targetId !== nodeId
                ),
                updatedAt: new Date().toISOString(),
              }
            : wf
        ),
        selectedNodeId: get().selectedNodeId === nodeId ? null : get().selectedNodeId,
      }));
    },

    moveNode: (workflowId, nodeId, x, y) => {
      set((state) => ({
        workflows: state.workflows.map((wf) =>
          wf.id === workflowId
            ? {
                ...wf,
                nodes: wf.nodes.map((n) => (n.id === nodeId ? { ...n, x, y } : n)),
              }
            : wf
        ),
      }));
    },

    addConnection: (workflowId, sourceId, targetId) => {
      const workflow = get().workflows.find((wf) => wf.id === workflowId);
      if (!workflow) return;

      const exists = workflow.connections.some(
        (c) => c.sourceId === sourceId && c.targetId === targetId
      );
      if (exists) return;

      const newConnection: NodeConnection = {
        id: `conn-${Date.now()}`,
        sourceId,
        targetId,
      };
      set((state) => ({
        workflows: state.workflows.map((wf) =>
          wf.id === workflowId
            ? { ...wf, connections: [...wf.connections, newConnection], updatedAt: new Date().toISOString() }
            : wf
        ),
      }));
    },

    deleteConnection: (workflowId, connectionId) => {
      set((state) => ({
        workflows: state.workflows.map((wf) =>
          wf.id === workflowId
            ? {
                ...wf,
                connections: wf.connections.filter((c) => c.id !== connectionId),
                updatedAt: new Date().toISOString(),
              }
            : wf
        ),
      }));
    },

    getWorkflowForIssueType: (issueType) => {
      return get().workflows.find((wf) => wf.issueType === issueType && wf.isDefault);
    },

    fetchWorkflows: async () => {
      set({ isLoading: true });
      try {
        const ds = await getActiveDs();
        const workflows = await ds.workflows.list();
        if (workflows.length > 0) {
          set({ workflows });
        }
      } catch (error) {
        console.error("Error fetching workflows, using fallback defaults:", error);
        // Keep fallback workflows
      } finally {
        set({ isLoading: false });
      }
    },

    getLabelsForStage: (stage, workflowId) => {
      const state = get();
      const workflow = workflowId
        ? state.workflows.find((w) => w.id === workflowId)
        : undefined;
      return workflow?.stageLabelMap?.[stage];
    },

    getActionsForStage: (stage, workflowId) => {
      const state = get();
      const workflow = workflowId
        ? state.workflows.find((w) => w.id === workflowId)
        : undefined;
      return workflow?.stageActions?.[stage];
    },

    resolveWorkflowForTask: (task) => {
      const state = get();
      if (task?.workflowId) {
        return state.workflows.find((w) => w.id === task.workflowId) as WorkflowConfig | undefined;
      }
      const issueType = task?.issueType || "feature";
      const defaultWf = state.workflows.find(
        (w) => w.issueType === issueType && w.isDefault
      );
      return defaultWf as WorkflowConfig | undefined;
    },
  })
);
